import {
  newPizza,
  T,
  basePriceCents,
  chosenIngredients,
  formatCents,
  ingredientGroups,
  ingredientPriceCents,
  ingredientQty,
  pizzaParts,
  pizzaCartId,
  pizzaLineName,
  pizzaPriceCents,
  pizzaProblems,
  sizesOf,
  toggleIngredient,
  tt,
  type BuilderIngredient,
  type PizzaConfig,
  type PizzaDraft,
  type PizzaPart,
} from '@agos/shared'
import { cn } from '@agos/ui'
import { Pizza, X } from 'lucide-react'
import { useState } from 'react'

/**
 * *Vlastná pizza* — FRONTEND.md §9.19.
 *
 * Legacy opens this from the pizza category and the AGOS customer app had no builder at all: the kombo
 * and the burger box were ported, this one was not.
 *
 * **Nothing here decides a price or a name.** Every figure comes from `@agos/shared`'s pizza-builder,
 * the same module the till's own builder uses, moved out of `manager-orders` so both can share it. A
 * pizza that costs one thing at the counter and another on the web is the defect that would otherwise
 * be one copy-paste away — and `pizzaCartId` matters twice over, because the server prices a negative
 * `productId` by bounding it (BACKEND.md §10A.9) and refuses an id it cannot place.
 */
export function CustomPizzaBuilder({
  config,
  ingredients,
  onClose,
  onAdd,
}: {
  config: PizzaConfig | null
  ingredients: readonly BuilderIngredient[]
  onClose: () => void
  onAdd: (line: {
    productId: number
    name: string
    unitPriceCents: number
    parts?: PizzaPart[]
  }) => void
}) {
  // §9.60 — opens on the franchise's default base, or blank where none is set.
  // §9.188 — the ingredients decide the cheese, so they are passed in. Without them the builder
  // opened with no base and no mozzarella, which is what the owner reported.
  const [draft, setDraft] = useState<PizzaDraft>(() => newPizza(config, ingredients))
  /**
   * §9.45 — start on the first size the franchise actually sells, so a franchise offering 40 and 50
   * starts on 40 and nothing is hard-coded.
   *
   * Set during render rather than in an effect, the way §9.40's waiter picker is: `useEffect` for
   * derived state is the cascading-setState the shell's lint rule refuses, and it would also fight
   * somebody who had just chosen a different size. Once, and only while nothing is chosen.
   */
  const firstSize = sizesOf(config)[0]?.cm ?? null
  if (draft.sizeCm === null && firstSize !== null) {
    setDraft((current) => (current.sizeCm === null ? { ...current, sizeCm: firstSize } : current))
  }

  const sizes = sizesOf(config)
  const problems = pizzaProblems(draft, config)
  const chosen = chosenIngredients(draft, ingredients)
  const totalCents = pizzaPriceCents(draft, config, ingredients)

  const add = () => {
    if (problems.length > 0) return
    const name = pizzaLineName(draft, config, ingredients)
    // §10A.14 — the size, the base, every topping and the crust, so the server can cap this line at
    // their sum. They sum to `totalCents` exactly, which is what makes the cap land on the true
    // price rather than refusing an honest basket.
    onAdd({
      productId: pizzaCartId(name),
      name,
      unitPriceCents: totalCents,
      parts: pizzaParts(draft, config, ingredients),
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-background w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          {/* §9.56 - the sentence that says what this screen is for. It was on the entry button and
            * not here, so it appeared on the way in and vanished on arrival. */}
          <div className="flex items-center gap-2.5 min-w-0">
            <Pizza className="w-5 h-5 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="font-bold leading-tight">
                <T>Vlastná pizza</T>
              </p>
              <p className="text-xs text-muted-foreground" data-custom-pizza-subtitle>
                <T>Zložte si pizzu podľa seba</T>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zavrieť')}
            className="p-2.5 rounded-full hover:bg-secondary min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-5 overflow-y-auto flex-1" data-custom-pizza>
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              <T>Veľkosť</T>
            </p>
            <div className="flex flex-wrap gap-2">
              {sizes.map((size) => (
                <button
                  key={size.cm}
                  type="button"
                  data-pizza-size={size.cm}
                  aria-pressed={draft.sizeCm === size.cm}
                  onClick={() => setDraft({ ...draft, sizeCm: size.cm })}
                  className={cn(
                    'px-3 py-2 rounded-xl border-2 text-sm font-bold transition-colors',
                    draft.sizeCm === size.cm
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/30',
                  )}
                >
                  {size.cm} cm
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              <T>Základ</T>
            </p>
            <div className="flex flex-wrap gap-2">
              {(config?.bases ?? []).map((base) => (
                <button
                  key={base.value}
                  type="button"
                  data-pizza-base={base.value}
                  aria-pressed={draft.baseValue === base.value}
                  onClick={() => setDraft({ ...draft, baseValue: base.value })}
                  className={cn(
                    'px-3 py-2 rounded-xl border-2 text-sm font-bold transition-colors',
                    draft.baseValue === base.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/30',
                  )}
                >
                  <T>{base.label}</T>
                  {draft.sizeCm !== null && (
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                      +{formatCents(basePriceCents(base.value, config, draft.sizeCm))}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* §9.25 - one heading per category, legacy's order, `sortOrder` inside it. This was a
           * flat list in payload order, and it offered the two *Základ* rows as toppings: the
           * pizza's own base, a second time, chargeable on top of the base already chosen. */}
          {ingredientGroups(ingredients).map((group) => (
            <div key={group.category}>
              <p
                className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2"
                data-pizza-ingredient-group={group.category}
              >
                <T>{group.category}</T>
              </p>
              <div className="space-y-1.5">
                {group.ingredients.map((ingredient) => {
                  // §9.43 — 0, 1 or 2 portions, not a checkbox.
                  const qty = ingredientQty(draft, ingredient.id)
                  const picked = qty > 0
                  // Priced for the chosen size, because an ingredient costs more on a bigger pizza and
                  // showing one number for all of them is how a total stops matching the tiles above it.
                  const cents =
                    draft.sizeCm === null ? null : ingredientPriceCents(ingredient, draft.sizeCm)
                  return (
                    <button
                      key={ingredient.id}
                      type="button"
                      data-pizza-ingredient={ingredient.id}
                      data-pizza-qty={qty}
                      aria-pressed={picked}
                      onClick={() => setDraft(toggleIngredient(draft, ingredient.id))}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 rounded-xl border text-left text-sm transition-colors',
                        qty === 0
                          ? 'border-border hover:bg-muted'
                          : qty === 1
                            ? 'border-primary bg-primary/5'
                            : 'border-orange-500 bg-orange-500/10',
                      )}
                    >
                      {/* The count rather than a tick: *1×* and *2×* are what the owner asked to see,
                        * and a second shade of a checkbox cannot say which of the two it is. */}
                      <span
                        className={cn(
                          'w-6 h-5 rounded border-2 flex items-center justify-center shrink-0 text-[10px] font-bold tabular-nums',
                          qty === 0
                            ? 'border-border text-transparent'
                            : qty === 1
                              ? 'bg-primary border-primary text-white'
                              : 'bg-orange-500 border-orange-500 text-white',
                        )}
                      >
                        {qty === 0 ? '+' : `${qty}×`}
                      </span>
                      <span className="flex-1 min-w-0 truncate" data-pizza-ingredient-name>
                        <T>{ingredient.name}</T>
                      </span>
                      {cents !== null && (
                        <span className="text-xs text-muted-foreground shrink-0">
                          +{formatCents(cents)}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}

          {/* §9.93 - no crust control. An `Okraj` row is an ordinary topping now, offered with
            * the rest above: the owner asked for *"the same logic as other ingrediencies… you can
            * select basically both of them"*, and one-of controls cannot say that. */}

          {problems.map((problem) => (
            <p key={problem} className="text-xs text-amber-500" data-pizza-problem>
              {problem}
            </p>
          ))}
        </div>

        <div className="px-4 py-3 border-t border-border shrink-0 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">
              <T>Spolu</T>
            </p>
            <p className="font-bold" data-pizza-total>
              {formatCents(totalCents)}
            </p>
            {chosen.length > 0 && (
              <p className="text-[11px] text-muted-foreground truncate" data-pizza-chosen>
                {chosen.map((ingredient) => ingredient.name).join(', ')}
              </p>
            )}
          </div>
          <button
            type="button"
            data-pizza-add
            disabled={problems.length > 0}
            onClick={add}
            className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm disabled:opacity-40"
          >
            <T>Pridať do košíka</T>
          </button>
        </div>
      </div>
    </div>
  )
}
